import React from 'react';
import { Checkbox, makeStyles, TableCell, TableRow, TableRowProps, Theme } from '@material-ui/core';
import cx from 'clsx';
import StatusChip from 'components/@common/StatusChip';
import { UserStatus, ProductCurriculum } from 'types/schema-generated-types';

const useStyles = makeStyles<Theme>(theme => ({
  checkboxCell: {
    width: '0.2rem',
    padding: 0,
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(0.5)
  },
  checkBox: {
    width: '14px',
    height: '14px',
    borderRadius: '1px',
    fontSize: '0.5rem',
    padding: 0,
    boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
    color: 'transparent'
  },
  cell: {
    width: '180px',
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: '0.9rem'
  },
  firstCell: {
    width: '200px',
    fontSize: '0.95rem',
    fontWeight: 600,
    paddingRight: 0,
    paddingLeft: theme.spacing(1.5)
  },
  lastCell: {
    paddingRight: theme.spacing(1.5)
  },
  thumbnail: {
    maxHeight: '26px',
    maxWidth: '26px'
  },
  padIcon: {
    paddingRight: theme.spacing(0.75)
  },
  noBorder: {
    border: 'none'
  },
  icons: {
    width: '200px'
  },
  input: {
    height: '15px',
    width: '15px'
  },
  gray: {
    backgroundColor: theme.palette.grey[200]
  },
  disabled: {
    color: theme.palette.secondary.main
  }
}));

export interface ClassroomRowData {
  id: number;
  name: string;
  educator?: string;
  coEducator?: string;
  productCurriculums?: ProductCurriculum[];
  status?: string;
}

export interface ClassroomSelectRowProps extends Omit<TableRowProps, 'children'> {
  classroomData: ClassroomRowData;
  noCellBorder?: boolean;
  handleCheck?: React.ChangeEventHandler<HTMLInputElement>;
  checked?: boolean;
  readOnly?: boolean;
}

const ClassroomSelectRow: React.FC<ClassroomSelectRowProps> = ({
  classroomData: { id, name, educator, coEducator, productCurriculums, status },
  noCellBorder,
  handleCheck,
  checked,
  readOnly
}) => {
  const classes = useStyles();
  const productIcons = productCurriculums
    ? productCurriculums.map(({ name, code, iconUrl }, index) => (
        <img
          key={code}
          src={iconUrl ?? ''}
          alt={name}
          className={cx(classes.thumbnail, {
            [classes.padIcon]: index < productCurriculums.length - 1
          })}
        />
      ))
    : null;
  const borderless = { [classes.noBorder]: noCellBorder };
  const gray = { [classes.gray]: checked };
  return (
    <TableRow className={classes.noBorder}>
      <TableCell className={cx(classes.checkboxCell, borderless, gray)}>
        <Checkbox
          value={id}
          disabled={readOnly}
          aria-label={`${name} Checkbox`}
          aria-checked={checked}
          aria-disabled={readOnly}
          checked={checked}
          onChange={handleCheck}
          classes={{
            disabled: classes.disabled
          }}
          className={classes.checkBox}
          size="small"
          color="secondary"
        />
      </TableCell>
      <TableCell className={cx(classes.firstCell, borderless, gray)}>{name}</TableCell>
      <TableCell className={cx(classes.cell, borderless, gray)}>{educator}</TableCell>
      <TableCell className={cx(classes.cell, borderless, gray)}>{coEducator}</TableCell>
      <TableCell className={cx(classes.cell, borderless, gray)}>
        <StatusChip status={status as UserStatus} size="small" />
      </TableCell>
      <TableCell className={cx(classes.cell, classes.icons, gray, classes.lastCell)}>
        {productIcons}
      </TableCell>
    </TableRow>
  );
};

export default ClassroomSelectRow;
